import { useCallback, useSyncExternalStore } from "react";

/**
 * Returns whether the engine evaluates media queries from script; jsdom does not.
 *
 * @returns `true` if `window.matchMedia` is a function.
 */
function canMatchMedia(): boolean {
  const matchMedia: unknown = Reflect.get(window, "matchMedia");
  const supported = typeof matchMedia === "function";

  return supported;
}

/**
 * Returns whether a media query matches and follows its changes, for example when a window is
 * resized past a breakpoint. Without `matchMedia` (tests, old engines) the query counts as not
 * matching, which is the phone layout of the app.
 *
 * @param query - The media query, for example `(min-width: 720px)`.
 * @returns `true` while the query matches.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (!canMatchMedia()) {
        return () => undefined;
      }

      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      const unsubscribe = (): void => {
        list.removeEventListener("change", onChange);
      };

      return unsubscribe;
    },
    [query],
  );
  const read = (): boolean => {
    const matches = canMatchMedia() && window.matchMedia(query).matches;
    return matches;
  };
  const matches = useSyncExternalStore(subscribe, read, () => false);

  return matches;
}
